import { useState } from 'react';
import { Plus, Info } from 'lucide-react';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Badge from '@/components/common/Badge';
import RuleCard from '@/components/rules/RuleCard';
import RuleForm from '@/components/rules/RuleForm';
import { useRules } from '@/hooks/useRules';

export default function Rules() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const { data: rules, isLoading } = useRules();

  const handleAdd = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingRule(null);
  };

  // Sort rules by priority (higher priority first)
  const sortedRules = rules ? [...rules].sort((a, b) => (b.priority || 0) - (a.priority || 0)) : [];
  const enabledCount = sortedRules.filter(r => r.enabled).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Rules</h1>
          <p className="text-muted-foreground mt-2">
            Configure email filtering rules
          </p>
        </div>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Rule
        </Button>
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              How Rules Work
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Rules are evaluated in order of priority (highest first). When an email matches a rule's conditions,
              the specified actions are applied. Use the toggle to enable/disable rules without deleting them.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Total Rules:</span>
          <Badge variant="secondary">{sortedRules.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Active:</span>
          <Badge variant="success">{enabledCount}</Badge>
        </div>
      </div>

      {/* Rules List */}
      {isLoading ? (
        <div className="py-12">
          <LoadingSpinner />
        </div>
      ) : sortedRules.length > 0 ? (
        <div className="space-y-3">
          {sortedRules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-muted-foreground mb-4">
            No rules configured yet.
          </p>
          <Button onClick={handleAdd} className="flex items-center gap-2 mx-auto">
            <Plus className="h-4 w-4" />
            Create Your First Rule
          </Button>
        </div>
      )}

      {/* Rule Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={editingRule ? 'Edit Rule' : 'Create Rule'}
        size="xl"
      >
        <RuleForm
          rule={editingRule}
          onSuccess={handleClose}
          onCancel={handleClose}
        />
      </Modal>
    </div>
  );
}
